import { OpeningHourEntry } from '../../types/locations';
import type { UseCase } from '../../utils/useCases';

export interface AccommodationDetail {
    guests?: number;
    /**
     * Per-unit facts (#2134). They share this JSON column with the
     * accommodation fields rather than getting columns of their own — see
     * `new-backend/admin/src/lib/inventory/objectAttributes.ts`. Written
     * through `PATCH /admin/inventory/objects/:assetId`, which MERGES; the
     * asset PUT replaces the whole column, so anything reading it for an edit
     * form must preserve keys it does not model.
     */
    serialNumber?: string;
    volumeCm3?: number;
    note?: string;
    beds?: number;
    baths?: number;
    rooms?: number;
    kitchen?: number;
    bedrooms?: number;
    outdoor?: string;
    amenities?: AccommodationDetailAmenity[];
    facilityDescription?: string;
    facilityDescriptions?: Record<string, string>;
    importantNote?: string;
    importantNotes?: Record<string, string>;
    policies?: AccommodationDetailPolicy[];
    floorNumber?: { en: string, de: string };
    floorName?: { en: string, de: string };
    floorDirection?: { en: string, de: string };
    apartmentType?: { en: string, de: string };
}

export interface AccommodationDetailAmenity {
    type: string;
    key: string;
    value: string | number;
    langValues?: Record<string, string>;
    icon: string;
}

export interface AccommodationDetailPolicy {
    title: string;
    titles?: Record<string, string>;
    description: string;
    descriptions?: Record<string, string>;
    icon: string;
}

export interface PricingDiscount {
    nights: [number, number];
    discount: number;
}

/**
 * Editable representation of a price scale row, used by the PricingSection
 * edit UI. The backend `PriceScale` shape is preserved on save via the
 * original scale id; newly added scales have no id.
 */
/**
 * A tariff row as the OBJECT page edits it — which is: only the price.
 *
 * The three fields below are not editable here and never rendered; they are
 * carried so the save can send the row back whole. `updatePriceScales` matches
 * an incoming row to an existing one on its full scope (days, full-day,
 * duration, start, end) and archives whatever it cannot match, so a payload
 * that omits the window does not leave the window alone — it archives the
 * windowed row and creates a window-less copy. 25 rows in production carry a
 * window (KGSt tool rental, Bundeskanzleramt), and `additional_days_price`
 * likewise was overwritten with null on every save from this page.
 */
export interface EditablePriceScale {
    id?: string;
    price: number;
    days?: string | null;
    durationInMinutes: number;
    isFullDay?: boolean;
    /** Passthrough — part of the row's scope, not editable here. */
    startTime?: string | null;
    /** Passthrough — part of the row's scope, not editable here. */
    endTime?: string | null;
    /** Passthrough — editable only in the Services price editor. */
    additionalDaysPrice?: number | null;
    /**
     * #1808. The customer class this rate applies to; null/absent is the
     * GENERAL rate that applies to every class.
     *
     * Part of the row's SCOPE, like days and the time window — the save path
     * matches an incoming row to an existing one on the whole scope and
     * archives what it cannot match, so this must round-trip.
     */
    customerClassId?: string | null;
}

export interface DecimalValue {
    s: number;
    e: number;
    d: number[];
}

export interface PriceScale {
    id: string;
    price: number | DecimalValue;
    days: string | null;
    startTime: string | null;
    endTime: string | null;
    durationInMinutes: number | null;
    isFullDay: boolean;
    additionalDaysPrice: number | DecimalValue | null;
    /** #1808 — set on class-specific rate rows; null/absent = the general rate. */
    customerClassId?: string | null;
}

export interface AssetAdditionalService {
    id: string;
    serviceName: string;
    langNames: Record<string, string> | null;
    unitPrice: number | DecimalValue;
    isPaymentCompulsory: boolean;
    quantity: boolean;
    /** #2873: per_booking | per_person | per_hour | per_day; null = per
     *  piece (unit price × chosen quantity). */
    priceUnit?: string | null;
    /** #1353 VAT category id; null = unassigned. */
    taxCategoryId?: string | null;
}

export interface AssetDoorMapping {
    door: number;
    doorMechanism: string;
}

export interface AssetBookingSummary {
    orderId: string;
    bookingNumber: string;
    useCase: string;
    startDate: string;
    endDate: string;
    paymentStatus: string;
    status: 'booked' | 'active' | 'finished' | 'canceled' | 'reserved' | 'incompleted';
}

export interface AssetIssueReportSummary {
    id: string;
    state: 'open' | 'in_progress' | 'on_hold';
    message: string | null;
    issueType: string | null;
    service: string | null;
    bookingNumber: string | null;
    createdAt: string;
}

export interface UpdateAssetDetailsRequest {
    name: string;
    floor?: string | null;
    accomodationdetail: AccommodationDetail;
    assetAdditionalServices: Array<{
        id?: string;
        serviceName: string;
        langNames: Record<string, string>;
        unitPrice: number;
        isPaymentCompulsory: boolean;
        quantity: boolean;
        /** #2873: '' or null clears back to per piece; absent = unchanged. */
        priceUnit?: string | null;
        /** #1353 VAT category id; '' = unassigned, absent = unchanged. */
        taxCategoryId?: string;
    }>;
    /** Per-asset length-of-stay discount tiers. Apartments only. */
    pricingDiscounts?: PricingDiscount[];
    /**
     * Weekly opening hours for the asset's location. Wipe-and-recreate
     * semantics: a missing field is a no-op, an empty array clears all
     * configured hours. Currently used for venues only.
     */
    openingHours?: OpeningHourEntry[];
}

/**
 * Payload for the one-shot asset creation endpoint. Creates address, location,
 * price category, group, and asset in one transaction, then uploads images to
 * S3 only if the transaction committed.
 *
 * Sent as multipart/form-data: the JSON payload in a `data` field, the
 * featured image in `featuredImage`, and additional images in `additionalImages`.
 */
export interface CreateAssetWithAddressRequest {
    name: string;
    /**
     * Floor / level designation, persisted to the dedicated `asset.floor`
     * column. First-class field — no longer derived solely from the name.
     */
    floor?: string | null;
    /** A room-family use case (`isRoomFamily`); the form offers only those. */
    useCase: UseCase;
    /**
     * An existing HOUSE to create the room in (plan
     * room-houses-and-combining-rooms §4 row 2). The room then takes the
     * house's location and opening hours, so `address` and `openingHours` are
     * not sent.
     */
    depotId?: string;
    /**
     * Create a new location although one with the identical address exists.
     * Without it the backend answers 409 `address_exists` and names the houses
     * standing there, so the operator decides rather than duplicates by accident.
     */
    confirmNewLocation?: boolean;
    address?: {
        street: string;
        houseNumber: string;
        postalCode: string;
        city: string;
        district: string;
        geoLat: number | null;
        geoLng: number | null;
    };
    accomodationdetail: AccommodationDetail;
    assetAdditionalServices?: Array<{
        serviceName: string;
        langNames: Record<string, string>;
        unitPrice: number;
        isPaymentCompulsory: boolean;
        quantity: boolean;
        /** #2873 — see the update type. */
        priceUnit?: string | null;
        /** #1353 VAT category id; '' = unassigned, absent = unchanged. */
        taxCategoryId?: string;
    }>;
    /**
     * D12 (PO 2026-09-28): the room product this object is created INTO — its
     * prices and long-stay discounts come from the product; the create flow
     * asks for neither.
     */
    productId: string;
    /** Weekly opening hours for the asset's location. Venues only. */
    openingHours?: OpeningHourEntry[];
    /** Local File objects — not serialised to JSON, appended to FormData. */
    featuredImage?: File | null;
    additionalImages?: File[];
}

export interface CreateAssetWithAddressResponse {
    id: string;
    featuredImage?: string | null;
    additionalImages?: string[];
    imageWarnings?: string[];
}

export type AssetState = 'available' | 'inUse' | 'blocked';

export interface LangNameEntry {
    title: string;
    description: string;
}

export interface AssetDetailsResponse {
    id: string;
    name: string;
    floor: string | null;
    state: AssetState;
    description: string | null;
    usedCounter: number;
    category: number | null;
    createdAt: string;
    updatedAt: string;
    archivedAt: string | null;
    accomodationdetail: AccommodationDetail | null;
    pricingDiscounts: PricingDiscount[] | null;
    additionalImages: string[];

    /** Pricing stream slice 1: the Product this object's cell offers and how
     *  many cells share it. `cellCount > 1` = the price is maintained at the
     *  PRODUCT (shared default Offer), so the room-scope price editor locks.
     *  Null on legacy stores (no product table) and pre-mirror groups. */
    productInfo?: {
        id: string;
        name: string | null;
        useCase: string | null;
        cellCount: number;
        /** #2923: the product category the product is filed under; null =
         *  none (or archived). */
        category?: { id: string, name: string | null } | null;
    } | null;

    Group: {
        id: string;
        name: string;
        langNames: Record<string, LangNameEntry> | null;
        compartmentTypes: string[];
        description: string;
        featuredImage: string | null;
        maxLimit: number | null;
        depositAmount: number | null;
        location: {
            id: string;
            name: string;
            services: string[];
            address: {
                street: string | null;
                houseNumber: string | null;
                city: string | null;
                postalCode: string | null;
                district: string | null;
                geoLat: number | DecimalValue | null;
                geoLng: number | DecimalValue | null;
            } | null;
            openingHours?: OpeningHourEntry[];
            /** #3219: the location's IANA zone — the clock this object's
             *  bookings, calendar and grid are read on. Absent on an older
             *  server (= the station default). */
            timeZone?: string;
        } | null;
        pricecategory: {
            id: string;
            name: string;
            pricescale: PriceScale[];
        } | null;
    } | null;

    assetAdditionalServices: AssetAdditionalService[];
    doormapping: AssetDoorMapping[];
    recentBookings: AssetBookingSummary[];
    openIssueReports: AssetIssueReportSummary[];
    /**
     * Whether the room's opening hours are shared with other rooms — a house
     * stands at its location (A1 a, `isLocationShared`). They are then shown
     * read-only, and an edit is refused by the backend. Absent from an older
     * backend.
     */
    openingHoursShared?: boolean;
    /** The house the room stands in, or null (absent from an older backend). */
    house?: { id: string, name: string } | null;
    /**
     * The object's PRODUCT as the product page shows it, decided on the server
     * by the one display rule (`bookedDisplay`, `@kolula/shared`). Null for an
     * object without a Group; absent from an older backend.
     */
    productIdentity?: {
        name: string | null;
        description: string;
        photo: string | null;
    } | null;
    /**
     * The photo the display rule shows for THIS object, and the row it came
     * from: a room's Group ('room'), an individually chosen object's own first
     * photo ('object'), a pooled unit's product ('product') or a product-less
     * Group ('group'). Absent from an older backend. See `objectCover`.
     */
    shownPhoto?: string | null;
    photoSource?: 'room' | 'object' | 'product' | 'group';
}

export interface UpdateAssetRequest {
    assetId: string;
    name: string;
    accomodationdetail?: AccommodationDetail | null;
}

/**
 * Convert a Prisma Decimal serialized value ({s, e, d}) to a number.
 */
export const decimalToNumber = (val: number | DecimalValue | null | undefined): number | null => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') return val;
    const { s, e, d } = val;
    const digits = d.join('');
    const num = parseFloat(digits) * Math.pow(10, e - digits.length + 1);
    return s === -1 ? -num : num;
};
